export default {
  common: {
    signIn: "Sign in",
    signOut: "Sign out",
    signingIn: "Signing in...",
    email: "Email",
    password: "Password",
    name: "Name",
    role: "Role",
    save: "Save Changes",
    saving: "Saving...",
    cancel: "Cancel",
    delete: "Delete",
    deleting: "Deleting...",
    search: "Search",
    edit: "Edit",
    back: "Go back",
    home: "Home",
    loading: "Loading...",
    sending: "Sending...",
    verify: "Verify",
    verifying: "Verifying...",
    updating: "Updating...",
    previous: "Previous",
    next: "Next",
    user: "User",
    coach: "Coach",
    admin: "Admin",
    status: "Status",
    active: "Active",
    invited: "Invited",
    createdAt: "Created At",
    actions: "Actions",
    currentPassword: "Current Password",
    newPassword: "New Password",
    confirmPassword: "Confirm Password",
    confirmNewPassword: "Confirm New Password",
    emailPlaceholder: "you@example.com",
    passwordPlaceholder: "••••••••",
    namePlaceholder: "John Doe",
    backToSignIn: "Back to sign in",
    forgotPassword: "Forgot password?",
    hasAccount: "Already have an account?",
  },

  validation: {
    invalidEmail: "Please enter a valid email address",
    invalidUrl: "Please enter a valid URL",
    invalidDate: "Please enter a valid date",
    passwordRequired: "Password is required",
    passwordMin8: "Password must be at least 8 characters",
    passwordsDoNotMatch: "Passwords do not match",
    nameRequired: "Please enter a name",
    currentPasswordRequired: "Current password is required",
    codeMustBe6Digits: "Code must be 6 digits",
    titleRequired: "Please enter a title",
    categoryRequired: "Please enter a category",
    descriptionRequired: "Please enter a description",
    locationRequired: "Please enter a location",
    aboutEmployerRequired: "Please describe the employer",
  },

  auth: {
    login: {
      title: "Sign in",
      subtitle: "Enter your credentials to access your account",
      inviteAccepted: "Account activated! You can now sign in.",
      accountDeleted: "Your account has been deleted.",
      userSessionActive:
        "You're signed in as {{email}} on the user platform. Sign in with your admin credentials below to access this dashboard.",
    },
    forgotPassword: {
      title: "Forgot your password?",
      subtitle: "Enter your email and we'll send you a reset link.",
      checkEmail: "Check your email",
      checkEmailDescription:
        "If an account exists for that email address, we've sent a password reset link.",
      sendResetLink: "Send reset link",
      rememberPassword: "Remember your password?",
    },
    resetPassword: {
      title: "Reset your password",
      subtitle: "Enter your new password below.",
      invalidLink: "Invalid reset link",
      invalidLinkDescription: "This password reset link is invalid or has expired.",
      requestNewLink: "Request a new link",
      passwordUpdated: "Password updated",
      passwordUpdatedDescription: "Your password has been reset successfully.",
      signInWithNewPassword: "Sign in with your new password",
      updatePassword: "Update password",
    },
    twoFactor: {
      title: "Two-factor authentication",
      subtitle: "Verify your identity to continue.",
      authenticatorApp: "Authenticator app",
      emailCode: "Email code",
      backupCode: "Backup code",
      totpDescription: "Open your authenticator app and enter the 6-digit code.",
      authenticationCode: "Authentication code",
      otpDescription: "We'll send a one-time code to your email address.",
      sendCodeToEmail: "Send code to email",
      backupDescription: "Enter one of your backup codes. Each code can only be used once.",
      resend: "Resend",
    },
    setup2fa: {
      title: "Set up two-factor authentication",
      subtitle: "Your organization requires 2FA. Set it up to continue.",
      required: "Required",
      activated: "Two-factor authentication is now active. Redirecting...",
    },
    acceptInvite: {
      title: "Set up your account",
      subtitle: "Welcome, {{name}}. Choose a password to activate your account.",
      activateAccount: "Activate account",
      activating: "Activating account...",
      passwordHint: "Must be at least 8 characters long",
      invalidLink: "This invite link is invalid or has expired",
      invalidLinkDescription:
        "The invite link you followed is no longer valid. Please ask an administrator to send you a new one.",
      alreadyAccepted: "Invite already used",
      alreadyAcceptedDescription:
        "This invite has already been used to set up an account. You can sign in directly.",
      signInToAccount: "Sign in to your account",
    },
  },

  profile: {
    title: "Profile",
    account: "Account",
    changePassword: "Change Password",
    passwordUpdated: "Password updated successfully.",
    twoFactor: {
      title: "Two-Factor Authentication",
      description: "Add an extra layer of security to your account.",
      enabled: "Enabled",
      disabled: "Disabled",
      setupPrompt: "Enter your password to begin setting up 2FA.",
      setUp: "Set up 2FA",
      scanQr: "Step 1 — Scan this QR code with your authenticator app",
      cantScan: "Can't scan? Enter this key manually in your app:",
      saveBackupCodes: "Step 2 — Save these backup codes",
      backupCodesNote: "Store these somewhere safe. Each code can only be used once.",
      copyCodes: "Copy all codes",
      copiedCodes: "Copied!",
      downloadCodes: "Download codes",
      savedCodesConfirm: "I have saved my backup codes in a safe place",
      verifyStep: "Step 3 — Verify with a code from your app",
      verifyAndActivate: "Verify & activate",
      activated: "Two-factor authentication is now active on your account.",
      yourBackupCodes: "Your backup codes",
      backupCodesWarning: "Store these somewhere safe. You won't be able to see them again.",
      disablePrompt: "Enter your password to disable two-factor authentication.",
      disable: "Disable 2FA",
      disabling: "Disabling...",
      disabled2fa: "2FA has been disabled.",
    },
    dangerZone: {
      title: "Danger Zone",
      description:
        "Permanently delete your account and all associated data. This action cannot be undone.",
      deleteAccount: "Delete Account",
      deleteConfirmTitle: "Delete your account?",
      deleteConfirmDescription:
        "This will permanently delete your account and all associated data. Type your email to confirm.",
      typeEmail: "Type your email to confirm",
      confirmDelete: "Permanently delete account",
    },
  },

  dashboard: {
    title: "Dashboard",
    welcome: "Welcome back, {{name}}",
    profileLink: "Profile",
    profileDescription: "Manage your account settings, password, and 2FA.",
    adminLink: "Admin Panel",
    adminDescription: "Manage users, view dashboard, and system settings.",
  },

  admin: {
    title: "Admin",
    dashboard: {
      title: "Dashboard",
      subtitle: "Overview of your admin panel",
    },
    users: {
      title: "Users",
      subtitle: "Manage user accounts and permissions",
      addUser: "Add User",
      searchPlaceholder: "Search by email...",
      noUsersFound: "No users found matching your search",
      noUsersYet: "No users yet",
      showingRange: "Showing {{from}} to {{to}} of {{total}} users",
      pageOf: "Page {{current}} of {{total}}",
      userCreated: "User created successfully",
      userUpdated: "User updated successfully",
      userDeleted: "User deleted successfully",
    },
    newUser: {
      title: "Add User",
      subtitle: "An invite link will be sent to the user so they can set their own password.",
      subtitleDirect: "The user will be able to log in immediately with the provided credentials.",
      backToUsers: "Back to Users",
      sendInvite: "Send Invite",
      sendingInvite: "Sending invite...",
      createUser: "Create User",
      creatingUser: "Creating user...",
      passwordOptionalLabel: "Password (optional)",
      passwordOptionalHint:
        "Leave blank to send an invite email. Fill in to set credentials directly.",
      testModeWarning:
        "Direct password setting bypasses the invite flow. The user can log in immediately without email verification. This is intended for test sessions only and will be removed in a future release.",
    },
    editUser: {
      title: "Edit User",
      subtitle: "Update user account details",
      backToUsers: "Back to Users",
      userId: "User ID",
      created: "Created",
      pendingInvite: "Pending Invite",
      pendingInviteDescription:
        "This user has not yet accepted their invite. You can resend the invite link.",
      resendInvite: "Resend Invite",
      resending: "Sending...",
      inviteResent: "Invite resent successfully",
      dangerZone: "Danger Zone",
      deleteWarning: "Once you delete a user, there is no going back. Please be certain.",
      cannotDeleteSelf:
        "You cannot delete your own account. Please ask another administrator to delete your account if needed.",
      cannotChangeOwnRole: "You cannot change your own role.",
      deleteUser: "Delete User",
      confirmDelete: "Are you sure you want to delete {{email}}? This action cannot be undone.",
    },
    sidebar: {
      appName: "Buddy",
      dashboard: "Dashboard",
      jobSeekers: "Job Seekers",
      users: "Users",
      professions: "Professions",
      vacancies: "Vacancies",
    },
    professions: {
      title: "Professions",
      subtitle: "Manage profession profiles used for matching",
      addProfession: "Add Profession",
      searchPlaceholder: "Search by name or sector...",
      noProfessionsFound: "No professions found matching your search",
      noProfessionsYet: "No professions yet",
      showingRange: "Showing {{from}} to {{to}} of {{total}} professions",
      pageOf: "Page {{current}} of {{total}}",
      professionCreated: "Profession created successfully",
      professionUpdated: "Profession updated successfully",
      professionDeleted: "Profession deleted successfully",
      columns: {
        title: "Name",
        category: "Sector",
        status: "Status",
        createdAt: "Created At",
        actions: "Actions",
      },
      status: {
        active: "Active",
        inactive: "Inactive",
      },
      fields: {
        title: "Name",
        titlePlaceholder: "e.g. Software Engineer",
        category: "Sector",
        categoryPlaceholder: "e.g. IT, Zorg, Onderwijs",
        description: "Description",
        descriptionPlaceholder: "Describe what this profession involves...",
        requirements: "Requirements",
        requirementsHint: "One requirement per line",
        requirementsPlaceholder:
          "Bachelor degree in Computer Science\n2+ years of experience\nFluent in Dutch and English",
        commaSeparatedHint: "Separate items with commas",
        legalRequirements: "Legal requirements",
        legalRequirementsPlaceholder: "e.g. VOG, driver's license, safety certificate",
        competencies: "Competencies",
        competenciesPlaceholder: "e.g. Energetic, teamwork, physical fitness, independence",
        interests: "Interests / enjoys",
        interestsPlaceholder: "e.g. Working with people, hands-on tasks, helping others",
        candidateRequirements: "Candidate requirements",
        candidateRequirementsPlaceholder:
          "e.g. Low-stimulus workplace, able to stand for long periods, able to sit for long periods",
        additionalRequirements: "Additional profession requirements",
        additionalRequirementsPlaceholder: "e.g. Flexible hours, shift work, outdoor work",
        languageRequirement: "Language requirement",
        languageRequirementPlaceholder: "e.g. Dutch B1, Dutch B2, no specific requirement",
        jobOutlook: "Job outlook",
        jobOutlookPlaceholder: "e.g. Promising, limited vacancies, high demand",
        video: "Profession Video",
        videoHint: "MP4, WebM, MOV, or OGG. Max 200 MB.",
        currentVideo: "Current video",
        replaceVideo: "Replace video",
        aiContext: "AI Matching Context (admin only)",
        aiContextHint:
          "Internal notes that help the AI match users to this profession. Not visible to users.",
        aiContextPlaceholder:
          "Personality traits, work style, related interests, anything that helps the AI score matches...",
        isActive: "Active",
        isActiveHint: "Inactive professions are hidden from users",
      },
    },
    newProfession: {
      title: "Add Profession",
      subtitle: "Create a new profession profile",
      backToProfessions: "Back to Professions",
      submit: "Create Profession",
      submitting: "Creating...",
    },
    editProfession: {
      title: "Edit Profession",
      subtitle: "Update profession details",
      backToProfessions: "Back to Professions",
      created: "Created",
      dangerZone: "Danger Zone",
      deleteWarning:
        "Deleting a profession also removes its video and unlinks all user matches and decisions. This cannot be undone.",
      deleteProfession: "Delete Profession",
      confirmDelete: 'Are you sure you want to delete "{{title}}"? This action cannot be undone.',
    },
    vacancies: {
      title: "Vacancies",
      subtitle: "Manage vacancies shown to users",
      addVacancy: "Add Vacancy",
      searchPlaceholder: "Search by title, category or location...",
      noVacanciesFound: "No vacancies found matching your search",
      noVacanciesYet: "No vacancies yet",
      showingRange: "Showing {{from}} to {{to}} of {{total}} vacancies",
      pageOf: "Page {{current}} of {{total}}",
      vacancyCreated: "Vacancy created successfully",
      vacancyUpdated: "Vacancy updated successfully",
      vacancyDeleted: "Vacancy deleted successfully",
      columns: {
        title: "Title",
        category: "Category",
        location: "Location",
        status: "Status",
        createdAt: "Created At",
        actions: "Actions",
      },
      status: {
        active: "Active",
        inactive: "Inactive",
      },
      sections: {
        jobDetails: "Job details",
        companyDetails: "Company details",
        jobRequirements: "Job requirements",
        offer: "Offer",
        applicationProcedure: "Application procedure",
        admin: "Admin",
      },
      fields: {
        title: "Job title",
        titlePlaceholder: "e.g. Junior Software Developer",
        category: "Category",
        categoryPlaceholder: "e.g. ICT, Zorg, Onderwijs",
        location: "Work location",
        locationPlaceholder: "e.g. Amsterdam",
        description: "Work content",
        descriptionPlaceholder:
          "Describe the role, responsibilities and what the candidate will do...",
        employer: "Employer",
        employerPlaceholder: "e.g. Brthrs B.V.",
        employerAddress: "Address",
        employerAddressPlaceholder: "Street, number, postcode, city",
        employerPhone: "Phone number",
        employerPhonePlaceholder: "e.g. +31 20 123 4567",
        employerWebsite: "Website",
        employerWebsitePlaceholder: "https://example.com",
        aboutEmployer: "Company description",
        aboutEmployerPlaceholder:
          "Tell candidates about the company: who they are, what they do and what makes them a great place to work...",
        education: "Education",
        educationPlaceholder: "e.g. HBO Informatica or equivalent",
        competencies: "Competencies",
        competenciesPlaceholder: "communication, problem solving, teamwork",
        commaSeparatedHint: "Comma-separated, e.g. communication, problem solving",
        driversLicense: "Driver's license",
        driversLicensePlaceholder: "e.g. B",
        requirements: "Other requirements",
        requirementsHint: "One requirement per line. These will be shown as chips.",
        requirementsPlaceholder:
          "HBO Informatica or equivalent\n2+ years of experience\nFluent in Dutch and English",
        salary: "Salary",
        salaryPlaceholder: "e.g. €2500 per month or €18 per hour",
        salaryHint: "Can be a monthly or hourly salary",
        hoursPerWeek: "Hours per week",
        hoursPerWeekPlaceholder: "e.g. 32-40",
        hoursPerWeekHint: "Can be a fixed number or a range",
        contractType: "Employment contract",
        contractTypePlaceholder: "e.g. Permanent, Temporary, Part-time",
        otherOffer: "Other offer",
        otherOfferPlaceholder: "Additional benefits, bonuses, training budget...",
        requestedDocuments: "Requested documents",
        requestedDocumentsPlaceholder: "CV\nMotivation letter\nReferences",
        requestedDocumentsHint: "One document per line.",
        applicationContactInfo: "Contact for questions",
        applicationContactInfoPlaceholder: "How candidates can reach out with questions",
        applicationContactPerson: "Application contact person",
        applicationContactPersonPlaceholder: "Name of the contact person",
        applicationWebsite: "Application website",
        applicationWebsitePlaceholder: "https://example.com/apply",
        applicationAddress: "Application address",
        applicationAddressPlaceholder: "Postal address for applications",
        applicationEmail: "Application email",
        applicationEmailPlaceholder: "apply@example.com",
        applicationDeadline: "Apply by",
        aiContext: "AI Matching Context (admin only)",
        aiContextHint:
          "Internal notes that help the AI match users to this vacancy. Not visible to users.",
        aiContextPlaceholder:
          "Personality fit, work style, ideal candidate profile, anything that helps the AI score matches...",
        isActive: "Active",
        isActiveHint: "Inactive vacancies are hidden from users",
      },
    },
    newVacancy: {
      title: "Add Vacancy",
      subtitle: "Create a new vacancy",
      backToVacancies: "Back to Vacancies",
      submit: "Create Vacancy",
      submitting: "Creating...",
    },
    editVacancy: {
      title: "Edit Vacancy",
      subtitle: "Update vacancy details",
      backToVacancies: "Back to Vacancies",
      created: "Created",
      dangerZone: "Danger Zone",
      deleteWarning:
        "Deleting a vacancy also unlinks all user matches and decisions. This cannot be undone.",
      deleteVacancy: "Delete Vacancy",
      confirmDelete: 'Are you sure you want to delete "{{title}}"? This action cannot be undone.',
    },
  },

  coach: {
    jobSeekers: {
      title: "Job Seekers",
      subtitle: "View user platform profiles for coaching and analysis",
      searchPlaceholder: "Search by name or email...",
      noJobSeekersFound: "No job seekers found matching your search",
      noJobSeekersYet: "No job seekers yet",
      showingRange: "Showing {{from}} to {{to}} of {{total}} job seekers",
      pageOf: "Page {{current}} of {{total}}",
      notProvided: "Not provided",
      onboardingComplete: "Onboarding complete",
      onboardingIncomplete: "Onboarding incomplete",
      columns: {
        residence: "Residence",
        onboarding: "Onboarding",
      },
    },
    jobSeekerDetails: {
      title: "Job seeker details",
      backToJobSeekers: "Back to Job Seekers",
      lastActivity: "Last activity",
      notAvailable: "Not available",
      answered: "Answered",
      notAnswered: "Not answered yet",
      yes: "Yes",
      no: "No",
      maybe: "Maybe",
      completed: "Completed",
      currentStep: "Step {{step}}",
      answeredOfTotal: "{{answered}} / {{total}} answered",
      modulesCompleted: "{{completed}} / {{total}} modules completed",
      decidedOfTotal: "{{decided}} / {{total}} decided",
      likedCount: "{{count}} liked",
      documentsAvailable: "Available documents",
      fileSizeKb: "{{size}} KB",
      fileSizeMb: "{{size}} MB",
      riasecScore: "Score {{score}}",
      matchScore: "{{score}}% match",
      decidedAt: "Decided on {{date}}",
      reason: "Reason",
      aiMatchExplanation: "AI match explanation",
      noDecisionYet: "The user has not decided on this match yet.",
      pendingDecision: "Pending",
      liked: "Liked",
      notLiked: "Not liked",
      elaboration: "Elaboration",
      aiFollowUp: "AI follow-up",
      summaryCards: {
        questionnaires: "Questionnaires",
        professions: "Professions",
        vacancies: "Vacancies",
        documents: "Documents",
      },
      profileSummary: {
        title: "Profile AI summary",
        description: "Generated from the user's questionnaire answers.",
        generatedAt: "Generated on {{date}}",
        empty: "No profile summary has been generated yet.",
      },
      questionnaires: {
        title: "Questionnaires",
        description: "All questionnaire questions are shown, including unanswered questions.",
        riasecTitle: "RIASEC answers",
        riasecDescription: "Interest scores used as part of the matching profile.",
        noRiasec: "No RIASEC questions are available.",
      },
      experiences: {
        title: "Experiences",
        description:
          "Structured education, work, course, internship, and volunteer entries from the questionnaire flow.",
        empty: "No experiences have been added yet.",
        addedCount: "{{count}} experiences added",
        organization: "Organization",
        institution: "Institution",
        fieldOfStudy: "Field of study",
        tasks: "Tasks",
        hasDiploma: "Has diploma",
        hasCertificate: "Has certificate",
        wantsRelatedWork: "Wants related work",
        wantsRelatedWorkExplanation: "Explanation",
        enjoyedAspects: "Enjoyed aspects",
        dislikedAspects: "Disliked aspects",
        reasonStopped: "Reason stopped",
        aiFollowUpEnjoyed: "AI follow-up about enjoyed aspects",
        aiFollowUpDisliked: "AI follow-up about disliked aspects",
        period: "{{start}} - {{end}}",
        periodCurrent: "{{start}} - present",
      },
      experienceType: {
        education: "Education",
        course: "Course",
        profession: "Profession",
        internship: "Internship",
        volunteer: "Volunteer work",
      },
      professions: {
        title: "Professions",
        description: "Displayed matches shown to the user. Matching status: {{status}}.",
        summaryTodo:
          "TODO: Add an AI summary explaining the chosen professions once this is generated.",
        empty: "No displayed profession matches are available yet.",
      },
      vacancies: {
        title: "Vacancies",
        description: "Displayed vacancy matches shown to the user. Matching status: {{status}}.",
        summaryTodo:
          "TODO: Add an AI summary explaining the vacancy matches once this is generated.",
        empty: "No displayed vacancy matches are available yet.",
      },
      documents: {
        title: "Documents",
        description: "Uploaded and generated documents for this job seeker.",
        uploadedCvs: "Uploaded CVs",
        generatedDocuments: "Generated documents",
        noUploadedCvs: "No uploaded CVs yet.",
        noGeneratedDocuments: "No generated CVs or motivational letters yet.",
        fileAvailable: "Generated file available",
        fileNotAvailable: "Generated file not available yet",
        download: "Download",
        forVacancy: "{{date}} for {{vacancy}}",
      },
      processingStatus: {
        notStarted: "Not started",
        pending: "Pending",
        processing: "Processing",
        completed: "Completed",
        failed: "Failed",
      },
      documentType: {
        resume: "Generated CV",
        motivationalLetter: "Motivational letter",
      },
      klantbeeld: {
        title: "Client profile",
        description: "Overview of the job seeker based on the questionnaire.",
        columns: {
          onderdelen: "Client profile parts",
          onderwerpen: "Client profile topics",
          items: "Client profile items",
        },
        empty: "—",
        phrases: {
          strong: "Strong: {{value}}",
          weaker: "Weaker: {{value}}",
          describedAs: "People describe me as: {{value}}",
          energy: "I get energy from: {{value}}",
          wantUnderstood: "I want others to understand about me: {{value}}",
          dropOut: "I sometimes drop out or stop because of: {{value}}",
          homeCalm: "Calm home situation",
          homeNotCalm: "Unsettled home situation",
          homeArrange: "If I started tomorrow, I still need to arrange: {{value}}",
          tasksLiked: "I like doing: {{value}}",
          tasksDisliked: "I don't really like doing: {{value}}",
          eduLiked: "Liked: {{value}}",
          eduDisliked: "Disliked: {{value}}",
          toelichting: "Explanation: {{value}}",
        },
        loadLabels: {
          staand: "I can do standing work without problems",
          zittend: "I can do sitting work without problems",
          lopend: "I can do walking work without problems",
          tillen: "Can lift",
          gezondheid: "Physical health",
        },
        work: {
          roleFull: "Worked as {{jobTitle}} at {{organization}}.",
          roleJob: "Worked as {{jobTitle}}.",
          roleCompany: "Worked at {{organization}}.",
          period: "Period: from {{start}} to {{end}}.",
          periodCurrent: "Period: since {{start}} (current role).",
          duration: "Duration: {{value}}.",
          tasks: "Tasks: {{tasks}}.",
          reason: "Reason for stopping: {{reason}}.",
          liked: "What they liked: {{liked}}.",
          disliked: "What they liked less: {{disliked}}.",
          detail: "Details: {{value}}.",
          years_one: "{{count}} year",
          years_other: "{{count}} years",
          months_one: "{{count}} month",
          months_other: "{{count}} months",
        },
        parts: {
          wieBenIk: "Who am I?",
          watWilIk: "What do I want?",
          watKanIk: "What can I do?",
          watHebIkGedaan: "What have I done?",
        },
        subjects: {
          algemeneInformatie: "General information",
          persoonlijkheid: "Personality",
          socialeContext: "Social context",
          gewenstWerk: "Desired work",
          werkwensen: "Work preferences",
          opleiding: "Education",
          hobbys: "Hobbies",
          vervoer: "Transport",
          belastbaarheid: "Workload capacity",
          werkervaring: "Work experience",
        },
        items: {
          voorEnAchternaam: "First and last name",
          woonplaats: "Place of residence",
          sterkeEigenschappen: "strong traits",
          minderSterkeEigenschappen: "weaker traits",
          overigeKaraktereigenschappen: "other character traits",
          competenties: "competencies",
          gezinssituatie: "family/living situation",
          beroep: "Occupation",
          taken: "Tasks",
          werkomgeving: "Work environment",
          werktijden: "Working hours",
          niveau: "level",
          richting: "field of study",
          opleidingAfgerond: "education completed/passed?",
          watVondJeErvan: "what did you think of it?",
          cursussenTraining: "courses/training",
          auto: "car",
          ov: "public transport",
          scooter: "scooter",
          fiets: "bicycle",
          fysiek: "Physical",
          mentaal: "Mental",
          bedrijven: "companies",
          vaardigheden: "Skills",
        },
      },
    },
  },

  errors: {
    somethingWentWrong: "Something went wrong",
    somethingWentWrongRetry: "Something went wrong. Please try again.",
    unexpectedError: "An unexpected error occurred. Please try again later.",
    accessDenied: "Access denied",
    accessDeniedDescription: "You don't have permission to view this page.",
    pageNotFound: "Page not found",
    pageNotFoundDescription: "The page you're looking for doesn't exist or has been moved.",
    tryAgain: "Try again",
    errorStatus: "Error {{status}}",
    failedToUpdatePassword: "Failed to update password.",
    failedToEnable2fa: "Failed to enable 2FA.",
    invalidCodeRetry: "Invalid code. Please try again.",
    failedToSendCode: "Failed to send code. Please try again.",
    invalidBackupCode: "Invalid backup code. Please try again.",
    failedToDisable2fa: "Failed to disable 2FA.",
    failedToDeleteAccount: "Failed to delete account.",
    invalidInviteLink: "Invalid invite link",
    inviteNoLongerValid: "This invite is no longer valid",
    cannotDeleteOwnAccount: "You cannot delete your own account",
    userNotFound: "User not found",
    emailAlreadyExists: "A user with this email already exists",
    professionNotFound: "Profession not found",
    vacancyNotFound: "Vacancy not found",
    directPasswordNotEnabled: "Direct password creation is not enabled in this environment.",
    tooManyRequests: "Too many requests. Please try again later.",
    invalidVideoType: "Invalid video format. Use MP4, WebM, MOV, or OGG.",
    videoTooLarge: "Video file is too large. Maximum 200 MB.",
    fileEmpty: "The selected file is empty.",
    fileTooLarge: "Video file is too large. Maximum 200 MB.",
    invalidFileType: "Invalid video format. Use MP4, WebM, MOV, or OGG.",
    fileContentMismatch:
      "The file contents do not match its name. Please upload a real video file.",
  },
} as const;
